import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, Flame, Lightning, Crown, Diamond } from "@phosphor-icons/react";
import { usePillars } from "@/contexts/PillarsContext";
import { useI18n } from "@/contexts/I18nContext";
import { compactCoins } from "@/components/HeaderBar";

export default function Leaderboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { pillars } = usePillars();
  const [scope, setScope] = useState("global");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const showSchool = user?.kind === "token" && user?.school_name;
  const scopes = useMemo(() => [
    { id: "global", label: "Global" },
    { id: "diamonds", label: "Diamonds" },
    ...pillars.map((p) => ({ id: p.slug, label: p.name })),
  ], [pillars]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get(`/leaderboard?scope=${scope}`);
        if (!cancelled) setData(data);
      } catch (e) { if (!cancelled) setErr(formatApiError(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [scope]);

  return (
    <div className="card min-w-0" data-testid="leaderboard-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold font-display flex items-center gap-2">
          <Trophy size={24} weight="duotone" color="#FFD600" /> {t("Leaderboard")}
        </h2>
        {data?.my_rank && (
          <span className="text-xs text-[#000000]">
            {t("Your rank:")} <span className="text-[#CCFF00] font-bold">#{data.my_rank}</span> {t("of")} {data.total_participants}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {scopes.map((s) => (
          <button
            key={s.id}
            data-testid={`leaderboard-scope-${s.id}`}
            onClick={() => setScope(s.id)}
            className="px-3 py-1.5 rounded-full text-xs font-display font-semibold whitespace-nowrap transition-all"
            style={{
              background: scope === s.id ? "#CCFF00" : "#DCDEE2",
              color: scope === s.id ? "#0A0D14" : "#52525B",
              border: "1px solid " + (scope === s.id ? "#CCFF00" : "#C4C8CE"),
              cursor: "pointer",
            }}
          >{t(s.label)}</button>
        ))}
        {showSchool && (
          <button
            data-testid="leaderboard-scope-school"
            onClick={() => setScope("school")}
            className="px-3 py-1.5 rounded-full text-xs font-display font-semibold whitespace-nowrap transition-all"
            style={{
              background: scope === "school" ? "#CCFF00" : "#DCDEE2",
              color: scope === "school" ? "#0A0D14" : "#52525B",
              border: "1px solid " + (scope === "school" ? "#CCFF00" : "#C4C8CE"),
              cursor: "pointer",
            }}
          >{user.school_name}</button>
        )}
      </div>

      {loading && <div className="text-[#000000] text-sm">{t("Loading...")}</div>}
      {err && <div className="text-[#FF5100] text-sm">{err}</div>}

      {data && data.top.length === 0 && (
        <div className="text-[#000000] text-sm py-6 text-center">
          {t("No one has earned coins in this scope yet. Be the first 🏆")}
        </div>
      )}

      <ol className="flex flex-col gap-1.5">
        {data?.top.map((row, i) => {
          const isMe = row.id === user?.id;
          return (
            <motion.li
              key={row.id}
              initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              data-testid={`leaderboard-row-${i}`}
              className="flex items-center gap-3 p-2.5 rounded-xl"
              style={{
                background: isMe ? "rgba(204,255,0,0.08)" : (i < 3 ? "rgba(255,214,0,0.04)" : "transparent"),
                border: "1px solid " + (isMe ? "rgba(204,255,0,0.4)" : "transparent"),
              }}
            >
              <div className="w-8 grid place-items-center font-display font-bold text-lg shrink-0"
                   style={{ color: i === 0 ? "#FFD600" : i === 1 ? "#52525B" : i === 2 ? "#FF8C42" : "#A1A1AA" }}>
                {i < 3 ? <Crown size={20} weight="fill" /> : `#${i + 1}`}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={row.flair_color ? { color: row.flair_color } : undefined} data-testid={`leaderboard-display-${i}`}>
                  {isMe ? t("you") : row.display}
                </div>
                {row.school && <div className="text-xs text-[#000000] truncate">{row.school}</div>}
              </div>
              {scope !== "global" && scope !== "school" && row.pillar_lessons_done !== undefined && (
                <div className="text-xs text-[#00E5FF] font-bold">{row.pillar_lessons_done} {t("done")}</div>
              )}
              <div className="flex items-center gap-2 shrink-0">
                {(scope === "diamonds" || (row.diamonds || 0) > 0) && (
                  <span className="flex items-center gap-1 text-[#7DD3FC] font-display font-bold text-sm" data-testid={`leaderboard-diamonds-${i}`}>
                    <Diamond size={14} weight="fill" /> {compactCoins(row.diamonds || 0)}
                  </span>
                )}
                <span className="flex items-center gap-1 text-[#FFD600] font-display font-bold text-sm">
                  <Lightning size={14} weight="duotone" /> {compactCoins(row.xp || 0)}
                </span>
                <span className="flex items-center gap-1 text-[#FF5100] font-display font-bold text-sm">
                  <Flame size={14} weight="duotone" /> {compactCoins(row.streak || 0)}
                </span>
              </div>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
}
